import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as L from 'leaflet';
import { ProjectService } from '../../services/project.service';
import { ProjectStateService } from '../../services/project-state.service';
import { ProjectLocationModel } from '../../models/project.model';

// Fix default marker icons broken by webpack
const iconDefault = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = iconDefault;

@Component({
  selector: 'app-maps',
  standalone: true,
  imports: [],
  templateUrl: './maps.html',
  styleUrl: './maps.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer') mapContainer!: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  private map: L.Map | null = null;
  private markerLayer = L.layerGroup();
  private resizeObserver: ResizeObserver | null = null;
  private locations: ProjectLocationModel[] = [];

  viewInitialized = false;

  constructor(
    private projectService: ProjectService,
    private projectStateService: ProjectStateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.projectService.getProjectLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (locations) => {
          this.locations = locations;
          this.cdr.markForCheck();
          this.pushSuggestions(this.projectStateService.getMapFilterMode());
          if (this.viewInitialized) {
            this.applyFilter(
              this.projectStateService.getMapFilterMode(),
              this.projectStateService.getPageSearch()
            );
          }
        },
        error: () => {
          this.cdr.markForCheck();
        }
      });

    combineLatest([
      this.projectStateService.mapFilterMode$,
      this.projectStateService.pageSearch$
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([mode, search]) => {
        this.pushSuggestions(mode);
        if (this.viewInitialized) {
          this.applyFilter(mode, search);
        }
      });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.initMap();
  }

  private initMap(): void {
    this.map = L.map(this.mapContainer.nativeElement, {
      center: [39.5, -98.35],
      zoom: 4
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map);

    this.markerLayer.addTo(this.map);

    this.resizeObserver = new ResizeObserver(() => {
      this.map?.invalidateSize();
    });
    this.resizeObserver.observe(this.mapContainer.nativeElement);

    requestAnimationFrame(() => {
      this.map?.invalidateSize();
      if (this.locations.length > 0) {
        this.applyFilter(
          this.projectStateService.getMapFilterMode(),
          this.projectStateService.getPageSearch()
        );
      }
    });
  }

  private pushSuggestions(mode: 'project' | 'user'): void {
    const suggestions = mode === 'project'
      ? [...new Set(this.locations.map(l => l.projectName || '').filter(v => v))]
      : [...new Set(this.locations.flatMap(l => l.users || []).filter(v => v))];
    this.projectStateService.setMapAllSuggestions(suggestions);
  }

  private applyFilter(mode: 'project' | 'user', searchText: string): void {
    this.markerLayer.clearLayers();

    const search = (searchText || '').toLowerCase().trim();

    const hasCoords = (l: ProjectLocationModel) => !!l.latitude && !!l.longitude;

    const filtered = search === ''
      ? this.locations.filter(l => hasCoords(l))
      : this.locations.filter(l => {
          if (!hasCoords(l)) return false;
          if (mode === 'project') {
            return (
              l.projectName?.toLowerCase().includes(search) ||
              l.projectNumber?.toLowerCase().includes(search)
            );
          } else {
            return l.users?.some(u => u.toLowerCase().includes(search)) ?? false;
          }
        });

    const bounds: L.LatLngTuple[] = [];
    for (const location of filtered) {
      const lat = parseFloat(location.latitude!);
      const lng = parseFloat(location.longitude!);
      if (isNaN(lat) || isNaN(lng)) continue;
      this.addMarker(location, lat, lng);
      bounds.push([lat, lng]);
    }

    if (bounds.length > 0) {
      this.fitBounds(bounds);
    }
  }

  private fitBounds(bounds: L.LatLngTuple[]): void {
    if (!this.map) return;
    if (bounds.length === 1) {
      this.map.setView(bounds[0], 10);
    } else {
      this.map.fitBounds(bounds, { padding: [40, 40] });
    }
  }

  private addMarker(location: ProjectLocationModel, lat: number, lng: number): void {
    const usersHtml = location.users && location.users.length > 0
      ? `<hr style="margin:6px 0;border:none;border-top:1px solid #eee"/>
         <span style="color:#666;font-size:0.85em">${location.users.join(', ')}</span>`
      : '';

    const popupContent = `
      <div style="min-width:180px">
        <strong>${location.projectName}</strong><br/>
        <span style="color:#666">#${location.projectNumber}</span><br/>
        ${location.customerName ? `<span>${location.customerName}</span><br/>` : ''}
        ${location.customerCity ? `<span>${location.customerCity}${location.customerState ? ', ' + location.customerState : ''}</span>` : ''}
        ${usersHtml}
      </div>
    `;

    L.marker([lat, lng])
      .addTo(this.markerLayer)
      .bindPopup(popupContent);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    this.projectStateService.setMapAllSuggestions([]);
    this.projectStateService.setMapFilterMode('project');
    this.projectStateService.setPageSearch('');
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
}
